import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getAtsApplicationsWithEdgeFunction,
} from '../api/applicationsWithEdgeFunction';
import {
  listInterviewsForJobWithEdgeFunction,
} from '../api/interviewsWithEdgeFunction';
import {
  KanbanColumns,
  AtsApplication,
  InterviewListItem,
  Candidate,
  KanbanColumnId,
} from '../types';
import { ATS_CONSTANTS } from '../constants';

export interface JobInfo {
  id: string;
  title: string;
  location: string;
  type: string;
  salary: string;
  posted: string;
  status: string;
  description: string;
  requirements: string[];
  totalPositions: number;
  hires: number;
  fulfilledDate?: string;
  outlet_id?: string;
}

export const INITIAL_JOB_INFO: JobInfo = {
  id: '',
  title: '',
  location: '',
  type: '',
  salary: '',
  posted: '',
  status: '',
  description: '',
  requirements: [],
  totalPositions: 0,
  hires: 0,
};

export const INITIAL_COLUMNS_STATE: KanbanColumns = {
  applied: { id: 'applied', title: 'Applied', items: [] },
  'in-consideration': { id: 'in-consideration', title: 'In Consideration', items: [] },
  accepted: { id: 'accepted', title: 'Accepted', items: [] },
  rejected: { id: 'rejected', title: 'Rejected', items: [] },
};

export const useATSData = (jobId: string | undefined, reloadTrigger: number) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [columns, setColumns] = useState<KanbanColumns>(INITIAL_COLUMNS_STATE);
  const [jobInfo, setJobInfo] = useState<JobInfo>(INITIAL_JOB_INFO);
  const [interviewItems, setInterviewItems] = useState<InterviewListItem[]>([]);

  // Utility functions for mapping
  const mapJobStatusToUi = (status: string) => {
    const map: { [key: string]: string } = { OPEN: 'Open', CLOSED: 'Closed', PAUSED: 'Paused' };
    return map[status] || 'Open';
  };

  // Load top-level job info from navigation state
  useEffect(() => {
    const jobFromState = location.state?.job;
    if (jobFromState) {
      const status = mapJobStatusToUi(jobFromState.status);
      const salaryDisplay = jobFromState.salary_range;
      const hires =
        jobFromState.application_counts?.ACCEPTED ?? 0;
      setJobInfo((prev) => ({
        ...prev,
        title: jobFromState.title || prev.title,
        status: status,
        salary: salaryDisplay,
        description: jobFromState.job_description || jobFromState.description || prev.description,
        ...(hires !== undefined && { hires: hires }),
        totalPositions: jobFromState.expected_hires || prev.totalPositions,
        ...(jobFromState.outlet_id != null && { outlet_id: jobFromState.outlet_id }),
      }));
    }
  }, [location.state]);

  // Fetch applications and interviews, then build columns
  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    (async () => {
      if (!jobId) return;

      // Fetch both in parallel
      const [applicationsRes, interviewsRes] = await Promise.all([
        getAtsApplicationsWithEdgeFunction(jobId),
        listInterviewsForJobWithEdgeFunction(jobId)
      ]);

      if (!mounted) return;

      const newColumns: KanbanColumns = JSON.parse(JSON.stringify(INITIAL_COLUMNS_STATE));
      let acceptedCount = 0;
      let fetchedInterviewItems: InterviewListItem[] = [];

      // Process Interviews
      if (!interviewsRes.error && interviewsRes.data) {
        fetchedInterviewItems = interviewsRes.data;
        setInterviewItems(fetchedInterviewItems);
      } else {
        console.error('[ATS] load interviews error', interviewsRes.error);
        setInterviewItems([]);
      }

      // Create a map for quick interview lookup
      const interviewMap: { [key: string]: any } = {};
      fetchedInterviewItems.forEach((item) => {
        if (item.id && item.interviewDetails) {
          interviewMap[item.id] = {
            interviewId: item.interviewId,
            interviewDetails: item.interviewDetails,
            candidate_id: item.candidate_id,
          };
        }
      });

      // Process Applications
      if (!applicationsRes.error && applicationsRes.data) {
        applicationsRes.data.forEach((app: AtsApplication) => {
          const statusToCol: { [key: string]: KanbanColumnId } = {
            APPLIED: ATS_CONSTANTS.COLUMNS.APPLIED,
            IN_CONSIDERATION: ATS_CONSTANTS.COLUMNS.IN_CONSIDERATION,
            ACCEPTED: ATS_CONSTANTS.COLUMNS.ACCEPTED,
            REJECTED: ATS_CONSTANTS.COLUMNS.REJECTED,
            HIRED: ATS_CONSTANTS.COLUMNS.ACCEPTED,
          };

          if (app.status === 'ACCEPTED' || app.status === 'HIRED') {
            acceptedCount++;
          }

          // Check if this application has an interview
          const interviewData = interviewMap[app.application_id];

          const candidate: Candidate = {
            kind: 'candidate',
            id: app.application_id,
            candidate_id: app.candidate_id,
            name: app.candidate_name,
            skillMatch: app.skill_match?.score ?? 0,
            matchingSkills: app.skill_match?.matched_skills ?? [],
            column: statusToCol[app.status] || ATS_CONSTANTS.COLUMNS.APPLIED,
            applied_at: app.applied_at,
            interviewId: interviewData?.interviewId || null,
            interviewDetails: interviewData?.interviewDetails || null,
            offerLetter: app.offer_letter,
          };

          if (newColumns[candidate.column]) {
            newColumns[candidate.column] = {
              ...newColumns[candidate.column],
              items: [...newColumns[candidate.column].items, candidate],
            };
          }
        });

        setColumns(newColumns);
        setJobInfo((prev) => ({
          ...prev,
          hires: acceptedCount,
        }));
      }

      setIsLoading(false);
    })();

    return () => {
      mounted = false;
    };
  }, [jobId, reloadTrigger]);

  // Enrich interviewItems with candidate_id from columns when columns are updated
  useEffect(() => {
    if (interviewItems.length === 0 || Object.keys(columns).length === 0) return;

    const candidateIdMap: { [key: string]: string } = {};
    const offerMap: { [key: string]: any } = {};

    Object.values(columns).forEach((column) => {
      if (column.items) {
        column.items.forEach((item) => {
          if (item.id) {
            if (item.candidate_id) {
              candidateIdMap[item.id] = item.candidate_id;
            }
            if (item.offerLetter !== undefined) {
              offerMap[item.id] = item.offerLetter;
            }
          }
        });
      }
    });

    const needsUpdate = interviewItems.some(
      (item) =>
        (!item.candidate_id && candidateIdMap[item.id]) ||
        (item.offerLetter === undefined && offerMap[item.id] !== undefined)
    );

    if (needsUpdate) {
      setInterviewItems((prevItems) =>
        prevItems.map((item) => ({
          ...item,
          candidate_id: item.candidate_id || candidateIdMap[item.id],
          offerLetter: item.offerLetter !== undefined ? item.offerLetter : offerMap[item.id],
        }))
      );
    }
  }, [columns, interviewItems]);

  return {
    isLoading,
    columns,
    setColumns,
    jobInfo,
    setJobInfo,
    interviewItems,
    setInterviewItems,
  };
};
